import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import classnames from 'classnames';
import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import styles from './styles.module.scss';
import { IProps } from './types';

const DateRangeSelector = (props: IProps) => {
  const { minDate, maxDate, startDate, endDate, onChange, defaultRange } =
    props;
  const [range, setRange] = useState<string>(defaultRange);
  const rangeTypes = ['1w', '1m', 'all', 'custom'];

  const onChangeRange = useCallback(
    (newRange: string) => {
      setRange(newRange);
      const newStartDate = moment(maxDate);
      switch (newRange) {
        case '1w':
          onChange(newStartDate.subtract(7, 'days').toISOString(), maxDate);
          break;
        case '1m':
          onChange(newStartDate.subtract(1, 'month').toISOString(), maxDate);
          break;
        case 'all':
          onChange(minDate, maxDate);
          break;
        default:
          break;
      }
    },
    [onChange, minDate, maxDate],
  );

  const onChangeDate = (start: string, end: string) => {
    onChange(start, end);
    onChangeRange('custom');
  };

  useEffect(() => {
    onChangeRange(defaultRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRange]);

  return (
    <div className={styles['date-range-wrapper']}>
      <div className={styles['date-range-buttons-wrapper']}>
        <div className={styles['date-range-buttons-label']}>DATE RANGE</div>
        <div className={styles['date-range-buttons']}>
          {rangeTypes.map((rangeType) => (
            <div
              className={classnames(
                styles['date-range-button'],
                styles[`${range === rangeType ? 'selected' : ''}`],
              )}
              key={rangeType}
              id={`date-range-button-${rangeType}`}
              data-testid={`date-range-button-${rangeType}`}
              onClick={() => onChangeRange(rangeType)}
            >
              {rangeType.charAt(0).toUpperCase() + rangeType.substring(1)}
            </div>
          ))}
        </div>
      </div>
      <div className={styles['date-range-input-wrapper']}>
        <div className={styles['date-range-input-box']}>
          <div className={styles['date-range-input-label']}>FROM</div>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              className={styles['date-picker']}
              disableToolbar
              variant="inline"
              format={'MMM dd, yyyy'}
              InputProps={{ readOnly: true }}
              margin="normal"
              id="date-picker-inline-from"
              data-testid="from-date-input-field"
              label=""
              value={startDate}
              onChange={(selected) =>
                onChangeDate(selected!.toISOString(), endDate)
              }
              minDate={minDate}
              maxDate={maxDate}
              PopoverProps={{
                ...({ 'data-testid': 'from-date-popover' } as any),
              }}
            />
          </MuiPickersUtilsProvider>
        </div>
        <div className={styles['date-range-input-box']}>
          <div className={styles['date-range-input-label']}>TO</div>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              className={styles['date-picker']}
              disableToolbar
              variant="inline"
              format={'MMM dd, yyyy'}
              InputProps={{ readOnly: true }}
              margin="normal"
              id="date-picker-inline-to"
              data-testid="to-date-input-field"
              label=""
              value={endDate}
              onChange={(selected) =>
                onChangeDate(startDate, selected!.toISOString())
              }
              minDate={minDate}
              maxDate={maxDate}
              PopoverProps={{
                ...({ 'data-testid': 'to-date-popover' } as any),
              }}
            />
          </MuiPickersUtilsProvider>
        </div>
      </div>
    </div>
  );
};

export default DateRangeSelector;
